import SiInformation from '@/icons/SiInformation';
import { Listbox, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { FC, Fragment, useCallback, useRef, useState } from 'react';
import { FiCheck, FiChevronDown } from 'react-icons/fi';
import { Tooltip } from 'react-tooltip';
import { twMerge } from 'tailwind-merge';
import { InputContainer } from '../InputContainer';
import { InputProps, withField } from '../withField';

export interface Property {
  id: string;
  name: string;
  disabled?: boolean;
  disabledReason?: string;
}

interface SelectInputProps {
  options: Property[];
  placeholder?: string;
  disabled?: boolean;
}

export const SelectInput: FC<InputProps<Property | null> & SelectInputProps> = ({
  options,
  onChange,
  value,
  placeholder,
  className,
  disabled
}) => {
  const listboxRef = useRef<HTMLDivElement>(null);

  const [showAbove, setShowAbove] = useState<boolean>(false);

  const calcPosition = useCallback(() => {
    const windowHeight = window.innerHeight;

    const buttonRect = listboxRef.current?.getBoundingClientRect();
    const buttonTop = buttonRect?.y;

    setShowAbove(buttonTop ? buttonTop > windowHeight * 0.7 : false);
  }, [listboxRef, setShowAbove]);

  return (
    <div ref={listboxRef}>
      <Listbox disabled={disabled} value={value} onChange={onChange}>
        {({ open }) => (
          <div
            className={twMerge(
              className,
              'relative text-sm sm:leading-6',
              disabled && 'opacity-50 pointer-events-none'
            )}
          >
            <Listbox.Button
              className="cursor-pointer hover:brightness-95 w-full"
              onClick={calcPosition}
            >
              <InputContainer className='px-3 py-2 pr-8'>
                {value ? (
                  <span className="block truncate">{value.name}</span>
                ) : (
                  placeholder && <span className="block truncate opacity-60">{placeholder}</span>
                )}
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <FiChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </InputContainer>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                className={twMerge(
                  showAbove ? 'bottom-[105%]' : '',
                  'bg-secondary-std absolute z-30 mt-1 max-h-60 w-full min-w-fit overflow-auto',
                  'rounded-md py-1 text-base shadow-lg sm:text-sm',
                  'ring-1 ring-black ring-opacity-5 focus:outline-none dark:ring-gray-700'
                )}
              >
                {options.map(option => (
                  <Listbox.Option
                    key={option.id}
                    className={({ active }) =>
                      clsx(
                        active ? 'bg-primary-50 text-primary-800' : 'text-secondary-std',
                        'relative select-none py-2 pl-3 pr-9',
                        option.disabled ? 'cursor-default' : 'cursor-pointer'
                      )
                    }
                    disabled={option.disabled}
                    value={option}
                  >
                    {({ selected, active }) => (
                      <div className="flex items-center space-x-2">
                        {option.disabledReason && (
                          <div>
                            <a data-tooltip-id={`disabled-reason-tooltip-${option.id}`}>
                              <SiInformation className="h-5 w-5" />
                            </a>

                            <Tooltip className="!fixed" id={`disabled-reason-tooltip-${option.id}`}>
                              <p>{option.disabledReason}</p>
                            </Tooltip>
                          </div>
                        )}

                        <span className={clsx(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                          {option.name}
                        </span>

                        {selected ? (
                          <span
                            className={clsx(
                              active ? 'text-white' : 'text-primary-600',
                              'absolute inset-y-0 right-0 flex items-center pr-4'
                            )}
                          >
                            <FiCheck className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </div>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        )}
      </Listbox>
    </div>
  );
};

export const SelectField = withField<Property | null, SelectInputProps>(SelectInput);
